import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SqfNav from '../../components/SqfNav';
import RichTextEditor from '../../components/RichTextEditor';
import foodSafetyService from '../../services/foodSafetyService';
import sqfService from '../../services/sqfService';
import sqfAiService from '../../services/sqfAiService';
import productService from '../../services/productService';
import { useLanguage, t } from '../../context/LanguageContext';

// ─── Section groups for sidebar ───────────────────────────────────────────────
const SECTION_GROUPS = {
  fda: [
    { label: 'Overview', keys: ['scope_facility', 'management_commitment'] },
    {
      label: 'Shared Library', keys: [
        'gmp_prerequisite', 'ssop_general', 'allergen_control', 'supplier_control',
        'food_defense', 'recall_plan', 'pest_control', 'employee_training',
        'equipment_list', 'water_quality', 'waste_management', 'environmental_monitoring',
      ],
    },
    {
      label: 'FDA Plan', keys: [
        'fda_product_descriptions', 'fda_flow_diagrams', 'fda_hazard_analysis',
        'fda_preventive_controls', 'fda_process_controls', 'fda_allergen_controls_pc',
        'fda_sanitation_controls', 'fda_supply_chain_controls', 'fda_allergen_matrix',
        'fda_pcqi_designation', 'fda_monitoring_records', 'fda_corrective_actions',
        'fda_verification', 'fda_reanalysis', 'fda_modifications_record',
      ],
    },
  ],
  usda: [
    { label: 'Overview', keys: ['scope_facility', 'management_commitment'] },
    {
      label: 'Shared Library', keys: [
        'gmp_prerequisite', 'ssop_general', 'allergen_control', 'supplier_control',
        'food_defense', 'recall_plan', 'pest_control', 'employee_training',
        'equipment_list', 'water_quality', 'waste_management', 'environmental_monitoring',
      ],
    },
    {
      label: 'USDA / HACCP', keys: [
        'usda_product_descriptions', 'usda_flow_diagrams', 'usda_hazard_analysis',
        'usda_haccp_plan_table', 'usda_prerequisite_programs', 'usda_allergen_matrix',
        'usda_haccp_coordinator', 'usda_monitoring_records', 'usda_corrective_actions',
        'usda_verification', 'usda_record_keeping', 'usda_modifications_record',
      ],
    },
  ],
};

function fmt(d) {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ─── Linked policy read-only view ─────────────────────────────────────────────
function LinkedPolicyView({ section, lang }) {
  const policy = section?.LinkedPolicy;
  const activeVersion = policy?.Versions?.find(v => v.id === policy.activeVersionId);
  const content = lang === 'es' ? (activeVersion?.contentEs || activeVersion?.content) : (activeVersion?.content || activeVersion?.contentEs);
  return (
    <div>
      <div className="alert alert-info border-start border-4 border-info py-2 px-3 mb-3 d-flex align-items-start gap-2">
        <span style={{ fontSize: 18, lineHeight: 1.4 }}>🔗</span>
        <div style={{ flex: 1 }}>
          <div>
            <strong>{t('Linked to SQF Policy:', 'Vinculada a Política SQF:', lang)}</strong>{' '}
            <span>{(lang === 'es' ? policy?.nameEs : null) || policy?.name || t('Unknown policy', 'Política desconocida', lang)}</span>
            {activeVersion && (
              <span className="badge bg-success ms-2" style={{ fontSize: 10 }}>v{activeVersion.versionNumber}</span>
            )}
          </div>
          <div className="small text-muted mt-1">
            {t('Content is pulled live from the linked policy. Edit the policy to update this section.',
               'El contenido se obtiene de la política vinculada. Edita la política para actualizar esta sección.', lang)}
          </div>
        </div>
      </div>
      {content ? (
        <div
          className="ql-editor border rounded p-3"
          style={{ background: '#fff', minHeight: 200 }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      ) : (
        <div className="border rounded p-4 text-center text-muted" style={{ background: '#fafafa' }}>
          {t('The linked policy has no published content yet.', 'La política vinculada no tiene contenido publicado aún.', lang)}
        </div>
      )}
    </div>
  );
}

// ─── Auto-generated section placeholder ───────────────────────────────────────
function AutoGeneratedSection({ sectionKey, lang }) {
  const descriptions = {
    fda_product_descriptions:  t('Auto-generated from products assigned to this plan.', 'Auto-generado de los productos asignados a este plan.', lang),
    usda_product_descriptions: t('Auto-generated from products assigned to this plan.', 'Auto-generado de los productos asignados a este plan.', lang),
    fda_allergen_matrix:       t('Auto-generated from product ingredients and allergen flags.', 'Auto-generado de los ingredientes y alérgenos de los productos.', lang),
    usda_allergen_matrix:      t('Auto-generated from product ingredients and allergen flags.', 'Auto-generado de los ingredientes y alérgenos de los productos.', lang),
    fda_modifications_record:  t('Auto-generated from the plan version history.', 'Auto-generado del historial de versiones del plan.', lang),
    usda_modifications_record: t('Auto-generated from the plan version history.', 'Auto-generado del historial de versiones del plan.', lang),
  };
  return (
    <div className="alert alert-info border-0 mb-0">
      <strong>⚙️ {t('Auto-generated section', 'Sección auto-generada', lang)}</strong>
      <div className="small mt-1 text-muted">{descriptions[sectionKey] || t('This section is generated automatically.', 'Esta sección se genera automáticamente.', lang)}</div>
    </div>
  );
}

// ─── Table JSON editor (simple editable grid) ─────────────────────────────────
// Read-only preview for a table_json draft before it's applied — same forgiving parse as
// TableJsonEditor, but no edit controls (editing happens after Apply, in the real editor).
function TableJsonPreview({ value }) {
  const parsed = useMemo(() => {
    try { return JSON.parse(value || '{}'); } catch { return { columns: [], rows: [] }; }
  }, [value]);
  const columns = parsed.columns || [];
  const rows = parsed.rows || [];
  if (!columns.length) {
    return <div className="text-muted small">{value}</div>;
  }
  return (
    <div className="table-responsive">
      <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
        <thead className="table-light">
          <tr>{columns.map((c, i) => <th key={i}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableJsonEditor({ value, onChange }) {
  const parsed = useMemo(() => {
    try { return JSON.parse(value || '{}'); } catch { return { columns: [], rows: [] }; }
  }, [value]);

  const save = (updated) => onChange(JSON.stringify(updated));

  const addRow = () => save({ ...parsed, rows: [...(parsed.rows || []), Array(parsed.columns?.length || 0).fill('')] });
  const removeRow = (i) => save({ ...parsed, rows: parsed.rows.filter((_, idx) => idx !== i) });
  const updateCell = (rowI, colI, val) => {
    const rows = parsed.rows.map((r, ri) => ri === rowI ? r.map((c, ci) => ci === colI ? val : c) : r);
    save({ ...parsed, rows });
  };
  const addColumn = () => {
    const col = prompt('Column name:');
    if (!col) return;
    save({ columns: [...(parsed.columns || []), col], rows: (parsed.rows || []).map(r => [...r, '']) });
  };
  const removeColumn = (ci) => save({
    columns: parsed.columns.filter((_, i) => i !== ci),
    rows: parsed.rows.map(r => r.filter((_, i) => i !== ci)),
  });

  return (
    <div>
      <div className="table-responsive mb-2">
        <table className="table table-bordered table-sm mb-0" style={{ fontSize: 13 }}>
          <thead className="table-light">
            <tr>
              {(parsed.columns || []).map((col, ci) => (
                <th key={ci} style={{ whiteSpace: 'nowrap' }}>
                  {col}
                  <button className="btn btn-link btn-sm p-0 ms-1 text-danger" style={{ fontSize: 10 }} onClick={() => removeColumn(ci)}>✕</button>
                </th>
              ))}
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {(parsed.rows || []).map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci}>
                    <input
                      className="form-control form-control-sm border-0 p-0"
                      value={cell}
                      onChange={e => updateCell(ri, ci, e.target.value)}
                      style={{ minWidth: 80 }}
                    />
                  </td>
                ))}
                <td className="text-center">
                  <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => removeRow(ri)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="d-flex gap-2">
        <button className="btn btn-outline-secondary btn-sm" onClick={addRow}>+ Row</button>
        <button className="btn btn-outline-secondary btn-sm" onClick={addColumn}>+ Column</button>
      </div>
    </div>
  );
}

// ─── Approval modal ───────────────────────────────────────────────────────────
function ApprovalModal({ planType, onClose, onApproved, lang }) {
  const [name, setName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [role, setRole] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) { setError(t('Signature name is required.', 'El nombre de firma es requerido.', lang)); return; }
    setSaving(true);
    try {
      const result = await foodSafetyService.approvePlan(planType, { signatureName: name.trim(), signatureDate: date, signatureRole: role, notes });
      onApproved(result);
    } catch (err) {
      setError(err?.response?.data?.message || t('Approval failed.', 'Error al aprobar.', lang));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">✍️ {t('Approve & Sign', 'Aprobar y Firmar', lang)}</h5>
            <button className="btn-close" onClick={onClose} disabled={saving} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="alert alert-warning py-2 small mb-3">
              <strong>{t('Warning:', 'Advertencia:', lang)}</strong>{' '}
              {t('Once approved, this version is permanently locked. Future changes will require creating a new draft.',
                 'Una vez aprobada, esta versión queda bloqueada permanentemente. Cambios futuros requerirán un nuevo borrador.', lang)}
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">{t('Typed Signature (Full Name)', 'Firma Escrita (Nombre Completo)', lang)} *</label>
              <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder={t('Your full name', 'Tu nombre completo', lang)} />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">{t('Signature Date', 'Fecha de Firma', lang)} *</label>
              <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">{t('Role', 'Cargo', lang)}</label>
              <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
                <option value="">{t('Select role…', 'Seleccionar cargo…', lang)}</option>
                {planType === 'fda' && <option value="PCQI">PCQI (Preventive Controls Qualified Individual)</option>}
                {planType === 'usda' && <option value="HACCP Coordinator">HACCP Coordinator</option>}
                <option value="Quality Manager">{t('Quality Manager', 'Gerente de Calidad', lang)}</option>
                <option value="Owner / President">{t('Owner / President', 'Propietario / Presidente', lang)}</option>
                <option value="Other">{t('Other', 'Otro', lang)}</option>
              </select>
            </div>
            <div className="mb-0">
              <label className="form-label fw-semibold">{t('Version Notes', 'Notas de la Versión', lang)}</label>
              <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('Reason for this version (optional)', 'Razón de esta versión (opcional)', lang)} />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>{t('Cancel', 'Cancelar', lang)}</button>
            <button className="btn btn-success btn-sm" onClick={handleSubmit} disabled={saving || !name.trim()}>
              {saving ? t('Signing…', 'Firmando…', lang) : `✍️ ${t('Sign & Approve', 'Firmar y Aprobar', lang)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm modal ────────────────────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel = 'Confirm', variant = 'danger', onConfirm, onClose }) {
  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
        <div className="modal-content shadow">
          <div className="modal-header border-bottom-0 pb-0">
            <h5 className="modal-title fw-semibold">{title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body pt-2 pb-0">{message}</div>
          <div className="modal-footer border-top-0">
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
            <button className={`btn btn-${variant} btn-sm`} onClick={() => { onConfirm(); onClose(); }}>
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main editor page ─────────────────────────────────────────────────────────
export default function FoodSafetyPlanEditorPage() {
  const { planType } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const [plan, setPlan]                     = useState(null);
  const [orderedSections, setOrderedSections] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');

  const [activeSectionKey, setActiveSectionKey] = useState(null);
  const [editContent, setEditContent]           = useState('');
  const [saving, setSaving]                     = useState(false);
  const [saveMsg, setSaveMsg]                   = useState('');
  const [dirty, setDirty]                       = useState(false);

  const [showApproval, setShowApproval] = useState(false);
  const [toast, setToast]               = useState(null);

  const [showPolicyPicker, setShowPolicyPicker] = useState(false);
  const [policies, setPolicies]               = useState([]);
  const [policySearch, setPolicySearch]       = useState('');
  const [linkingSaving, setLinkingSaving]     = useState(false);

  // AI import state
  const [importStep, setImportStep]           = useState(null); // null | 'upload' | 'uploading' | 'processing' | 'review'
  const [importProgress, setImportProgress]   = useState('');
  const [importSections, setImportSections]   = useState([]);
  const [importSelected, setImportSelected]   = useState({});
  const [importApplying, setImportApplying]   = useState(false);
  const importFileRef                         = useRef(null);

  // AI generate section state
  const [showGenerate, setShowGenerate]       = useState(false);
  const [generateNotes, setGenerateNotes]     = useState('');
  const [generateStep, setGenerateStep]       = useState('idle'); // idle | running | preview
  const [generateProgress, setGenerateProgress] = useState('');
  const [generateDraft, setGenerateDraft]     = useState('');

  // Confirm modal state
  const [confirmModal, setConfirmModal]       = useState(null);

  // Products state
  const [planProducts, setPlanProducts]       = useState([]);
  const [allProducts, setAllProducts]         = useState([]);
  const [productsLoaded, setProductsLoaded]   = useState(false);
  const [productSearch, setProductSearch]     = useState('');
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [editingProduct, setEditingProduct]   = useState(null); // null | { ppId, productId, name, intendedUse, distributionMethod, storageConds, shelfLife, regulatoryCategory, allergens }
  const [productSaving, setProductSaving]     = useState(false);
  const [pickerSelected, setPickerSelected]   = useState({}); // productId → true
  const [rulesResult, setRulesResult]         = useState(null); // null | { loading, rules }
  const [autoGenPreview, setAutoGenPreview]   = useState(null); // null | { step:'running'|'done', content, progress }
  const [autoGenOverride, setAutoGenOverride] = useState({}); // sectionKey → true (manual edit mode)

  // Share links state
  const [showShareModal, setShowShareModal]   = useState(false);
  const [shareLinks, setShareLinks]           = useState([]);
  const [shareLinksLoading, setShareLinksLoading] = useState(false);
  const [shareExpiry, setShareExpiry]         = useState(15);
  const [shareNote, setShareNote]             = useState('');
  const [shareSaving, setShareSaving]         = useState(false);
  const [shareCreated, setShareCreated]       = useState(null); // URL after creation
  const [revoking, setRevoking]               = useState(null); // token being revoked

  const sectionRef = useRef({});

  const showToast = useCallback((message, variant = 'success') => {
    setToast({ message, variant });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await foodSafetyService.getPlan(planType);
      setPlan(data.plan);
      setOrderedSections(data.orderedSections || []);
      // Store content for quick access
      data.orderedSections?.forEach(s => {
        sectionRef.current[s.key] = s.section?.content || '';
      });
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to load plan.', 'Error al cargar el plan.', lang));
    } finally {
      setLoading(false);
    }
  }, [planType, lang]);

  useEffect(() => { load(); }, [load]);

  // When section changes, load content into editor
  useEffect(() => {
    if (!activeSectionKey) return;
    const sec = orderedSections.find(s => s.key === activeSectionKey);
    setEditContent(sec?.section?.content || '');
    setDirty(false);
    setSaveMsg('');
  }, [activeSectionKey]); // eslint-disable-line

  const handleSave = async () => {
    if (!activeSectionKey) return;
    setSaving(true);
    try {
      await foodSafetyService.saveSection(planType, activeSectionKey, editContent);
      setSaveMsg(t('Saved', 'Guardado', lang));
      setDirty(false);
      // Update local state
      setOrderedSections(prev => prev.map(s => {
        if (s.key !== activeSectionKey) return s;
        return { ...s, section: { ...(s.section || {}), content: editContent } };
      }));
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (err) {
      showToast(err?.response?.data?.message || t('Save failed.', 'Error al guardar.', lang), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleApproved = (result) => {
    setShowApproval(false);
    showToast(`✅ ${t('Plan approved as', 'Plan aprobado como', lang)} V${result.version?.versionNumber}`);
    load();
  };

  const openShareModal = async () => {
    setShareCreated(null);
    setShareNote('');
    setShareExpiry(15);
    setShareLinksLoading(true);
    setShowShareModal(true);
    try {
      const links = await foodSafetyService.getShareLinks(planType);
      setShareLinks(links || []);
    } catch {
      setShareLinks([]);
    } finally {
      setShareLinksLoading(false);
    }
  };

  const handleCreateShareLink = async () => {
    setShareSaving(true);
    try {
      const link = await foodSafetyService.createShareLink(planType, { expiryDays: shareExpiry, note: shareNote.trim() || null });
      const url = `${window.location.origin}/share/food-safety/${link.token}`;
      setShareCreated(url);
      setShareLinks(prev => [{ ...link, token: link.token, expiresAt: link.expiresAt, note: link.note, createdAt: link.createdAt }, ...prev]);
    } catch (err) {
      showToast(err?.response?.data?.message || t('Failed to create share link.', 'Error al crear el enlace.', lang), 'danger');
    } finally {
      setShareSaving(false);
    }
  };

  const handleRevokeShareLink = async (token) => {
    setRevoking(token);
    try {
      await foodSafetyService.revokeShareLink(token);
      setShareLinks(prev => prev.map(l => l.token === token ? { ...l, revokedAt: new Date().toISOString() } : l));
    } catch {
      showToast(t('Failed to revoke link.', 'Error al revocar el enlace.', lang), 'danger');
    } finally {
      setRevoking(null);
    }
  };

  const openPolicyPicker = async () => {
    try {
      const list = await sqfService.getPolicies();
      setPolicies(list || []);
      setPolicySearch('');
      setShowPolicyPicker(true);
    } catch {
      showToast(t('Failed to load policies.', 'Error al cargar políticas.', lang), 'danger');
    }
  };

  const handleLinkPolicy = async (policyId) => {
    setLinkingSaving(true);
    try {
      await foodSafetyService.linkSection(planType, activeSectionKey, policyId);
      setShowPolicyPicker(false);
      showToast(t('Section linked to policy.', 'Sección vinculada a la política.', lang));
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || t('Link failed.', 'Error al vincular.', lang), 'danger');
    } finally {
      setLinkingSaving(false);
    }
  };

  const handleUnlinkPolicy = () => {
    showConfirm({
      title: t('Remove Policy Link', 'Eliminar Vínculo de Política', lang),
      message: t(
        'Remove the link to this SQF Policy? The section will revert to manual editing.',
        '¿Eliminar el vínculo con esta política SQF? La sección volverá a edición manual.', lang
      ),
      confirmLabel: t('Remove Link', 'Eliminar Vínculo', lang),
      variant: 'warning',
      onConfirm: async () => {
        setLinkingSaving(true);
        try {
          await foodSafetyService.unlinkSection(planType, activeSectionKey);
          showToast(t('Policy link removed.', 'Vínculo eliminado.', lang));
          await load();
        } catch (err) {
          showToast(err?.response?.data?.message || t('Unlink failed.', 'Error al desvincular.', lang), 'danger');
        } finally {
          setLinkingSaving(false);
        }
      },
    });
  };

  // ── AI import handlers ────────────────────────────────────────────────────────
  const handleImportFile = async (file) => {
    if (!file) return;
    setImportStep('uploading');
    setImportProgress(`Uploading ${file.name}…`);
    try {
      const { fileUrl, mimeType } = await sqfAiService.uploadFileForAi(file);
      setImportStep('processing');
      setImportProgress('Upload complete. Starting AI extraction…');
      const sections = await foodSafetyService.importPlan(planType, { fileUrl, mimeType }, (msg) => setImportProgress(msg));
      if (!sections?.length) {
        showToast(t('No sections could be extracted from this document.', 'No se encontraron secciones en este documento.', lang), 'warning');
        setImportStep('upload');
        return;
      }
      // Pre-select all extracted sections
      const sel = {};
      sections.forEach(s => { sel[s.key] = true; });
      setImportSections(sections);
      setImportSelected(sel);
      setImportStep('review');
    } catch (err) {
      showToast(err?.message || t('Import failed.', 'Error al importar.', lang), 'danger');
      setImportStep('upload');
    }
  };

  const handleApplyImport = async () => {
    const toApply = importSections.filter(s => importSelected[s.key]);
    if (!toApply.length) return;
    setImportApplying(true);
    try {
      for (const s of toApply) {
        await foodSafetyService.saveSection(planType, s.key, s.content);
      }
      showToast(`✅ ${toApply.length} ${t('sections imported.', 'secciones importadas.', lang)}`);
      setImportStep(null);
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || t('Apply failed.', 'Error al aplicar.', lang), 'danger');
    } finally {
      setImportApplying(false);
    }
  };

  // ── AI generate section handlers ──────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerateStep('running');
    setGenerateProgress(t('Preparing context…', 'Preparando contexto…', lang));
    try {
      const content = await foodSafetyService.generateSection(
        planType,
        activeSectionKey,
        { notes: generateNotes, lang },
        (msg) => setGenerateProgress(msg),
      );
      setGenerateDraft(content);
      setGenerateStep('preview');
    } catch (err) {
      showToast(err?.message || t('Generation failed.', 'Error al generar.', lang), 'danger');
      setGenerateStep('idle');
    }
  };

  const handleApplyGenerate = () => {
    setEditContent(generateDraft);
    setDirty(true);
    setShowGenerate(false);
    setGenerateStep('idle');
    setGenerateNotes('');
    setGenerateDraft('');
  };

  const showConfirm = useCallback((opts) => setConfirmModal(opts), []);

  // ── Products handlers ──────────────────────────────────────────────────────
  const loadPlanProducts = useCallback(async () => {
    try {
      const [pps, allProds] = await Promise.all([
        foodSafetyService.getProducts(planType),
        productService.getProducts().then(r => r.products || r || []),
      ]);
      setPlanProducts(pps);
      setAllProducts(allProds);
      setProductsLoaded(true);
    } catch {
      showToast(t('Failed to load products.', 'Error al cargar productos.', lang), 'danger');
    }
  }, [planType, lang, showToast]);

  const openProductPanel = useCallback(async () => {
    if (!productsLoaded) await loadPlanProducts();
    setActiveSectionKey('__products__');
    setDirty(false);
  }, [productsLoaded, loadPlanProducts]);

  const openProductPicker = () => {
    setProductSearch('');
    setPickerSelected({});
    setShowProductPicker(true);
  };

  const handleAssignProducts = async () => {
    const ids = Object.keys(pickerSelected).filter(id => pickerSelected[id]);
    if (!ids.length) return;
    setProductSaving(true);
    try {
      const results = await Promise.all(
        ids.map(productId => foodSafetyService.addProduct(planType, { productId }))
      );
      setPlanProducts(prev => [...prev, ...results]);
      setShowProductPicker(false);
      setPickerSelected({});
    } catch (err) {
      showToast(err?.response?.data?.message || t('Failed to assign products.', 'Error al asignar productos.', lang), 'danger');
    } finally {
      setProductSaving(false);
    }
  };

  const handleRemoveProduct = (pp) => {
    showConfirm({
      title: t('Remove Product', 'Eliminar Producto', lang),
      message: t(`Remove "${pp.Product?.name || pp.productId}" from this plan?`, `¿Eliminar "${pp.Product?.name || pp.productId}" de este plan?`, lang),
      confirmLabel: t('Remove', 'Eliminar', lang),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await foodSafetyService.removeProduct(planType, pp.id);
          setPlanProducts(prev => prev.filter(p => p.id !== pp.id));
        } catch {
          showToast(t('Failed to remove product.', 'Error al eliminar producto.', lang), 'danger');
        }
      },
    });
  };

  const handleSaveProductDetails = async () => {
    if (!editingProduct) return;
    setProductSaving(true);
    try {
      const { ppId, intendedUse, distributionMethod, storageConds, shelfLife, regulatoryCategory, allergens, ingredients } = editingProduct;
      const updated = await foodSafetyService.updateProduct(planType, ppId, { intendedUse, distributionMethod, storageConds, shelfLife, regulatoryCategory, allergens, ingredients });
      setPlanProducts(prev => prev.map(p => p.id === ppId ? { ...p, ...updated } : p));
      setEditingProduct(null);
      showToast(t('Product details saved.', 'Detalles del producto guardados.', lang));
    } catch {
      showToast(t('Failed to save product details.', 'Error al guardar detalles.', lang), 'danger');
    } finally {
      setProductSaving(false);
    }
  };

  const handleCheckRules = async () => {
    if (!editingProduct?.ppId) return;
    setRulesResult({ loading: true, rules: [] });
    try {
      const rules = await foodSafetyService.checkProductRules(planType, editingProduct.ppId, {
        allergens: editingProduct.allergens,
        ingredients: editingProduct.ingredients,
      });
      setRulesResult({ loading: false, rules: rules || [] });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || t('Rule check failed.', 'Error al verificar reglas.', lang);
      setRulesResult({ loading: false, rules: [], error: msg });
    }
  };

  const handleAutoGenPreview = async () => {
    setAutoGenPreview({ step: 'running', content: '', progress: '' });
    try {
      const content = await foodSafetyService.generateSection(
        planType, activeSectionKey, { notes: '', lang },
        (msg) => setAutoGenPreview(prev => prev ? { ...prev, progress: msg } : null),
      );
      setAutoGenPreview({ step: 'done', content, progress: '' });
    } catch (err) {
      showToast(err?.message || t('Preview generation failed.', 'Error al generar vista previa.', lang), 'danger');
      setAutoGenPreview(null);
    }
  };

  const handleApplyAutoGen = (content) => {
    setEditContent(content);
    setAutoGenOverride(prev => ({ ...prev, [activeSectionKey]: true }));
    setAutoGenPreview(null);
    setDirty(true);
  };

  const activeSection = orderedSections.find(s => s.key === activeSectionKey);
  const planLabel = planType === 'fda' ? 'FDA Food Safety Plan' : 'USDA HACCP Plan';
  const groups = SECTION_GROUPS[planType] || [];

  // Compute completion stats
  const totalSections  = orderedSections.filter(s => s.contentType !== 'auto_generated').length;
  const filledSections = orderedSections.filter(s => s.contentType !== 'auto_generated' && s.section?.content?.trim()).length;
  const pct = totalSections > 0 ? Math.round((filledSections / totalSections) * 100) : 0;

  return (
    <div>
      <SqfNav />

      <div style={{ display: 'flex', height: 'calc(100vh - 116px)' }}>
        {/* ── Sidebar ── */}
        <div style={{ width: 260, minWidth: 260, borderRight: '1px solid #dee2e6', overflowY: 'auto', background: '#f8f9fa', padding: '12px 0' }}>
          <div className="px-3 mb-3">
            <button className="btn btn-link btn-sm p-0 text-muted text-decoration-none mb-2" onClick={() => navigate('/sqf/food-safety')}>
              ← {t('Back to Plans', 'Volver a Planes', lang)}
            </button>
            <div className="fw-bold" style={{ fontSize: 13 }}>{planLabel}</div>
            {plan && (
              <span className={`badge ${plan.status === 'approved' ? 'bg-success' : 'bg-warning text-dark'} mt-1`} style={{ fontSize: 10 }}>
                {plan.status === 'approved' ? t('Approved', 'Aprobado', lang) : t('Draft', 'Borrador', lang)}
              </span>
            )}
            {/* Completion bar */}
            <div className="mt-2">
              <div className="d-flex justify-content-between" style={{ fontSize: 10, color: '#888' }}>
                <span>{t('Completion', 'Completado', lang)}</span>
                <span>{filledSections}/{totalSections}</span>
              </div>
              <div className="progress mt-1" style={{ height: 4 }}>
                <div className="progress-bar bg-success" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="px-3 text-muted small">{t('Loading…', 'Cargando…', lang)}</div>
          ) : (
            groups.map(group => (
              <div key={group.label} className="mb-1">
                <div className="px-3 py-1" style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>
                  {group.label === 'Shared Library' ? `📚 ${group.label}` : group.label}
                </div>
                {group.keys.map(key => {
                  const sec = orderedSections.find(s => s.key === key);
                  if (!sec) return null;
                  const hasContent = !!sec.section?.content?.trim() || !!sec.section?.linkedPolicyId;
                  const isActive = activeSectionKey === key;
                  const isAuto = sec.contentType === 'auto_generated';
                  const isLinked = !!sec.section?.linkedPolicyId;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        if (!dirty) { setActiveSectionKey(key); return; }
                        showConfirm({
                          title: t('Unsaved Changes', 'Cambios Sin Guardar', lang),
                          message: t('You have unsaved changes. Discard and continue?', '¿Descartar los cambios y continuar?', lang),
                          confirmLabel: t('Discard', 'Descartar', lang),
                          variant: 'warning',
                          onConfirm: () => setActiveSectionKey(key),
                        });
                      }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '5px 12px 5px 16px',
                        fontSize: 12, border: 'none', cursor: 'pointer',
                        background: isActive ? '#e8f0fe' : 'transparent',
                        color: isActive ? '#1a73e8' : '#333',
                        fontWeight: isActive ? 600 : 400,
                        borderLeft: isActive ? '3px solid #1a73e8' : '3px solid transparent',
                      }}
                    >
                      <span style={{ marginRight: 6, fontSize: 10 }}>
                        {isAuto ? '⚙️' : isLinked ? '🔗' : hasContent ? '✅' : '○'}
                      </span>
                      {sec.title}
                      {sec.shared && <span className="ms-1" style={{ fontSize: 9, color: '#888' }}>shared</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}

          {/* Products entry */}
          {plan && (
            <div className="mb-1 mt-2">
              <div className="px-3 py-1" style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>
                {t('Plan Details', 'Detalles del Plan', lang)}
              </div>
              <button
                onClick={() => {
                  if (!dirty) { openProductPanel(); return; }
                  showConfirm({
                    title: t('Unsaved Changes', 'Cambios Sin Guardar', lang),
                    message: t('You have unsaved changes. Discard and continue?', '¿Descartar los cambios y continuar?', lang),
                    confirmLabel: t('Discard', 'Descartar', lang),
                    variant: 'warning',
                    onConfirm: openProductPanel,
                  });
                }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '5px 12px 5px 16px',
                  fontSize: 12, border: 'none', cursor: 'pointer',
                  background: activeSectionKey === '__products__' ? '#e8f0fe' : 'transparent',
                  color: activeSectionKey === '__products__' ? '#1a73e8' : '#333',
                  fontWeight: activeSectionKey === '__products__' ? 600 : 400,
                  borderLeft: activeSectionKey === '__products__' ? '3px solid #1a73e8' : '3px solid transparent',
                }}
              >
                <span style={{ marginRight: 6, fontSize: 10 }}>📦</span>
                {t('Products', 'Productos', lang)}
                {planProducts.length > 0 && (
                  <span className="badge bg-primary ms-1" style={{ fontSize: 9 }}>{planProducts.length}</span>
                )}
              </button>
            </div>
          )}

          {/* Sidebar actions */}
          {plan && (
            <div className="px-3 mt-3 pt-3 border-top d-flex flex-column gap-2">
              <button
                className="btn btn-outline-primary btn-sm w-100"
                onClick={() => { setImportStep('upload'); if (importFileRef.current) importFileRef.current.value = ''; }}
              >
                📂 {t('Import from PDF', 'Importar desde PDF', lang)}
              </button>
              <button className="btn btn-success btn-sm w-100" onClick={() => setShowApproval(true)}>
                ✍️ {t('Approve & Sign', 'Aprobar y Firmar', lang)}
              </button>
              {plan.activeVersionId && (
                <>
                  <button
                    className="btn btn-outline-secondary btn-sm w-100"
                    onClick={() => window.open(`/sqf/food-safety/${planType}/print?from=editor`, '_blank')}
                  >
                    🖨 {t('Print Plan', 'Imprimir Plan', lang)}
                  </button>
                  <button className="btn btn-outline-secondary btn-sm w-100" onClick={openShareModal}>
                    🔗 {t('Share Plan', 'Compartir Plan', lang)}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Main content area ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', maxWidth: 900 }}>
          {error && <div className="alert alert-danger">{error}</div>}

          {!activeSectionKey ? (
            <div className="text-center py-5 text-muted">
              <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
              <h5>{t('Select a section from the sidebar to begin editing', 'Selecciona una sección del panel lateral para empezar', lang)}</h5>
              <p className="small">{t('Sections with', 'Las secciones con', lang)} ✅ {t('have content.', 'tienen contenido.', lang)}</p>
            </div>
          ) : activeSectionKey === '__products__' ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="fw-bold mb-0">📦 {t('Products', 'Productos', lang)}</h5>
                  <div className="text-muted small mt-1">
                    {t('Products assigned to this plan are used to auto-generate product description and allergen matrix sections.',
                       'Los productos asignados a este plan se usan para generar automáticamente las secciones de descripción de producto y matriz de alérgenos.', lang)}
                  </div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={openProductPicker}>
                  + {t('Assign Product', 'Asignar Producto', lang)}
                </button>
              </div>

              {planProducts.length === 0 ? (
                <div className="text-center py-5 border rounded bg-light text-muted">
                  <div style={{ fontSize: 40 }}>📦</div>
                  <p className="mt-2 mb-3">{t('No products assigned yet.', 'Aún no hay productos asignados.', lang)}</p>
                  <button className="btn btn-primary btn-sm" onClick={openProductPicker}>
                    + {t('Assign your first product', 'Asignar tu primer producto', lang)}
                  </button>
                </div>
              ) : (
                <div>
                  {planProducts.map(pp => (
                    <div key={pp.id} className="card mb-3 shadow-sm">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <div className="fw-semibold" style={{ fontSize: 15 }}>{pp.Product?.name || `Product #${pp.productId}`}</div>
                            {pp.Product?.description && (
                              <div className="text-muted small">{pp.Product.description}</div>
                            )}
                          </div>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => {
                                setRulesResult(null);
                                const defaultRegCat = planType === 'usda' ? 'USDA FSIS 9 CFR 417 (HACCP)' : 'FDA 21 CFR 117 (FSMA HARPC)';
                                const catalogIngredients = (pp.Product?.ProductIngredients || [])
                                  .map(pi => {
                                    const ing = pi.Ingredient;
                                    if (!ing) return null;
                                    const subs = Array.isArray(ing.subIngredients) ? ing.subIngredients : [];
                                    if (subs.length > 0) {
                                      return `${ing.name} (${subs.map(s => s.name).filter(Boolean).join(', ')})`;
                                    }
                                    return ing.name;
                                  }).filter(Boolean).join(', ');
                                setEditingProduct({
                                  ppId: pp.id,
                                  productId: pp.productId,
                                  name: pp.Product?.name,
                                  intendedUse: pp.intendedUse || '',
                                  distributionMethod: pp.distributionMethod || '',
                                  storageConds: pp.storageConds || '',
                                  shelfLife: pp.shelfLife || '',
                                  regulatoryCategory: pp.regulatoryCategory || defaultRegCat,
                                  allergens: pp.allergens || '',
                                  ingredients: pp.ingredients || catalogIngredients,
                                  productIngredients: pp.Product?.ProductIngredients || [],
                                });
                              }}
                            >
                              ✏️ {t('Edit', 'Editar', lang)}
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleRemoveProduct(pp)}
                            >
                              ✕ {t('Remove', 'Eliminar', lang)}
                            </button>
                          </div>
                        </div>
                        <div className="row g-2" style={{ fontSize: 12 }}>
                          {[
                            [t('Intended Use', 'Uso Previsto', lang), pp.intendedUse],
                            [t('Distribution', 'Distribución', lang), pp.distributionMethod],
                            [t('Storage', 'Almacenamiento', lang), pp.storageConds],
                            [t('Shelf Life', 'Vida Útil', lang), pp.shelfLife],
                            [t('Regulatory', 'Regulatorio', lang), pp.regulatoryCategory],
                          ].map(([label, val]) => val ? (
                            <div key={label} className="col-auto">
                              <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
                                <span className="text-muted me-1">{label}:</span>{val}
                              </span>
                            </div>
                          ) : null)}
                        </div>
                        {!pp.intendedUse && !pp.distributionMethod && !pp.storageConds && !pp.shelfLife && (
                          <div className="text-muted small mt-1">
                            {t('No food safety details added yet. Click Edit to add intended use, storage conditions, and more.',
                               'Aún no hay detalles de inocuidad. Haz clic en Editar para agregar uso previsto, condiciones de almacenamiento y más.', lang)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : activeSection ? (
            <>
              {/* Section header */}
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h5 className="fw-bold mb-0">{activeSection.title}</h5>
                  <div className="mt-1 d-flex gap-2 flex-wrap">
                    {activeSection.shared && (
                      <span className="badge bg-info text-dark" style={{ fontSize: 10 }}>
                        📚 {t('Shared Library — applies to both plans', 'Biblioteca Compartida — aplica a ambos planes', lang)}
                      </span>
                    )}
                    <span className="badge bg-light text-muted border" style={{ fontSize: 10 }}>
                      {activeSection.contentType}
                    </span>
                  </div>
                </div>
                {activeSection.contentType === 'auto_generated' && (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {autoGenOverride[activeSectionKey] || activeSection.section?.content ? (
                      <>
                        {saveMsg && <span className="text-success small">{saveMsg}</span>}
                        {dirty && <span className="text-warning small">● {t('Unsaved', 'Sin guardar', lang)}</span>}
                        <button className="btn btn-outline-secondary btn-sm" onClick={handleAutoGenPreview}>
                          👁 {t('Regenerate Preview', 'Regenerar Vista Previa', lang)}
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => {
                            showConfirm({
                              title: t('Clear Manual Override', 'Limpiar Edición Manual', lang),
                              message: t('Remove your manual content and revert to auto-generated?', '¿Eliminar el contenido manual y volver a auto-generado?', lang),
                              confirmLabel: t('Clear', 'Limpiar', lang),
                              variant: 'danger',
                              onConfirm: async () => {
                                await foodSafetyService.saveSection(planType, activeSectionKey, '');
                                setAutoGenOverride(prev => { const n = { ...prev }; delete n[activeSectionKey]; return n; });
                                await load();
                              },
                            });
                          }}
                        >✕ {t('Clear Override', 'Limpiar', lang)}</button>
                        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !dirty}>
                          {saving ? t('Saving…', 'Guardando…', lang) : t('Save', 'Guardar', lang)}
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-outline-primary btn-sm" onClick={handleAutoGenPreview}>
                          👁 {t('Preview', 'Vista Previa', lang)}
                        </button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => {
                          setAutoGenOverride(prev => ({ ...prev, [activeSectionKey]: true }));
                          setEditContent('');
                          setDirty(false);
                        }}>
                          ✏️ {t('Edit Manually', 'Editar Manualmente', lang)}
                        </button>
                      </>
                    )}
                  </div>
                )}
                {activeSection.contentType !== 'auto_generated' && (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {activeSection.section?.linkedPolicyId ? (
                      <>
                        <button
                          className="btn btn-outline-warning btn-sm"
                          onClick={handleUnlinkPolicy}
                          disabled={linkingSaving}
                        >
                          {linkingSaving ? t('Unlinking…', 'Desvinculando…', lang) : `✂️ ${t('Unlink', 'Desvincular', lang)}`}
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => navigate(`/sqf/policies/${activeSection.section.linkedPolicyId}/edit`)}
                        >
                          ↗ {t('Go to Policy', 'Ver Política', lang)}
                        </button>
                      </>
                    ) : (
                      <>
                        {saveMsg && <span className="text-success small">{saveMsg}</span>}
                        {dirty && <span className="text-warning small">● {t('Unsaved', 'Sin guardar', lang)}</span>}
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => { setShowGenerate(true); setGenerateStep('idle'); setGenerateNotes(''); setGenerateDraft(''); }}
                          disabled={saving}
                        >
                          ✨ {t('Generate', 'Generar', lang)}
                        </button>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={openPolicyPicker}
                          disabled={saving}
                        >
                          🔗 {t('Link to Policy', 'Vincular a Política', lang)}
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !dirty}>
                          {saving ? t('Saving…', 'Guardando…', lang) : t('Save', 'Guardar', lang)}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Section editor */}
              {activeSection.contentType === 'auto_generated' && !autoGenOverride[activeSectionKey] && !activeSection.section?.content ? (
                <AutoGeneratedSection sectionKey={activeSectionKey} lang={lang} />
              ) : activeSection.contentType === 'auto_generated' ? (
                <div>
                  <div className="alert alert-info border-start border-4 border-info py-2 px-3 mb-3 d-flex align-items-start gap-2">
                    <span style={{ fontSize: 18, lineHeight: 1.4 }}>✏️</span>
                    <div style={{ flex: 1 }}>
                      <strong>{t('Manual override active', 'Edición manual activa', lang)}</strong>
                      <div className="small text-muted">
                        {t('You are editing this section manually. Use "Clear Override" to revert to auto-generation.',
                           'Estás editando esta sección manualmente. Usa "Limpiar" para volver a la generación automática.', lang)}
                      </div>
                    </div>
                  </div>
                  <RichTextEditor
                    value={editContent}
                    onChange={val => { setEditContent(val); setDirty(true); setSaveMsg(''); }}
                    onImageUpload={async (file) => { const { fileUrl } = await sqfAiService.uploadFileForAi(file); return fileUrl; }}
                    minHeight={360}
                  />
                </div>
              ) : activeSection.section?.linkedPolicyId ? (
                <LinkedPolicyView section={activeSection.section} lang={lang} />
              ) : activeSection.contentType === 'richtext' ? (
                <div>
                  <RichTextEditor
                    value={editContent}
                    onChange={val => { setEditContent(val); setDirty(true); setSaveMsg(''); }}
                    onImageUpload={async (file) => {
                      const { fileUrl } = await sqfAiService.uploadFileForAi(file);
                      return fileUrl;
                    }}
                    minHeight={360}
                  />
                  {activeSection.section?.lastEditedAt && (
                    <div className="text-muted small mt-2">
                      {t('Last saved', 'Último guardado', lang)}: {fmt(activeSection.section.lastEditedAt)}
                    </div>
                  )}
                </div>
              ) : activeSection.contentType === 'table_json' ? (
                <div>
                  <TableJsonEditor
                    value={editContent}
                    onChange={val => { setEditContent(val); setDirty(true); setSaveMsg(''); }}
                  />
                  {activeSection.section?.lastEditedAt && (
                    <div className="text-muted small mt-2">
                      {t('Last saved', 'Último guardado', lang)}: {fmt(activeSection.section.lastEditedAt)}
                    </div>
                  )}
                </div>
              ) : activeSection.contentType === 'image' ? (
                <div className="border rounded p-4 text-center text-muted" style={{ background: '#fafafa' }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>🖼️</div>
                  <p className="mb-2">{t('Upload process flow diagrams or scanned images.', 'Sube diagramas de flujo o imágenes escaneadas.', lang)}</p>
                  <span className="badge bg-secondary">{t('Image upload coming soon', 'Carga de imágenes próximamente', lang)}</span>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {/* AI Import modal */}
      {importStep && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className={`modal-dialog modal-dialog-centered ${importStep === 'review' ? 'modal-xl' : 'modal-lg'}`}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📂 {t('Import Food Safety Plan from PDF', 'Importar Plan de Inocuidad desde PDF', lang)}</h5>
                {importStep !== 'uploading' && importStep !== 'processing' && (
                  <button className="btn-close" onClick={() => setImportStep(null)} disabled={importApplying} />
                )}
              </div>

              {/* Upload step */}
              {importStep === 'upload' && (
                <div className="modal-body">
                  <p className="text-muted small mb-3">
                    {t(
                      'Upload an existing Food Safety Plan or HACCP Plan document (PDF or Word). Claude will extract the content and map it to the plan sections. You can review and choose which sections to apply.',
                      'Sube un documento de Plan de Inocuidad o HACCP existente (PDF o Word). Claude extraerá el contenido y lo mapeará a las secciones del plan. Puedes revisar y elegir qué secciones aplicar.',
                      lang
                    )}
                  </p>
                  <input
                    ref={importFileRef}
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="form-control"
                    onChange={e => e.target.files[0] && handleImportFile(e.target.files[0])}
                  />
                  <div className="text-muted small mt-2">
                    {t('Supported: PDF, Word (.docx)', 'Compatible: PDF, Word (.docx)', lang)}
                  </div>
                </div>
              )}

              {/* Uploading / Processing step */}
              {(importStep === 'uploading' || importStep === 'processing') && (
                <div className="modal-body text-center py-4">
                  <div className="spinner-border text-primary mb-3" />
                  <div className="fw-semibold mb-1">
                    {importStep === 'uploading'
                      ? t('Uploading document…', 'Subiendo documento…', lang)
                      : t('AI is analyzing your document…', 'La IA está analizando tu documento…', lang)}
                  </div>
                  <div className="text-muted small">{importProgress}</div>
                  {importStep === 'processing' && (
                    <div className="text-muted small mt-2">
                      {t('This may take 30–60 seconds depending on document size.', 'Esto puede tomar 30–60 segundos según el tamaño del documento.', lang)}
                    </div>
                  )}
                </div>
              )}

              {/* Review step */}
              {importStep === 'review' && (
                <>
                  <div className="modal-body p-0">
                    <div className="px-3 pt-3 pb-2 border-bottom d-flex justify-content-between align-items-center">
                      <div>
                        <span className="fw-semibold">{importSections.length}</span>{' '}
                        {t('sections extracted. Select which to apply:', 'secciones extraídas. Selecciona cuáles aplicar:', lang)}
                      </div>
                      <div className="d-flex gap-2">
                        <button className="btn btn-link btn-sm p-0" onClick={() => {
                          const all = {};
                          importSections.forEach(s => { all[s.key] = true; });
                          setImportSelected(all);
                        }}>{t('Select all', 'Seleccionar todo', lang)}</button>
                        <button className="btn btn-link btn-sm p-0" onClick={() => setImportSelected({})}>
                          {t('None', 'Ninguno', lang)}
                        </button>
                      </div>
                    </div>
                    <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                      {importSections.map(sec => (
                        <div key={sec.key} className="border-bottom px-3 py-2">
                          <div className="d-flex align-items-start gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input mt-1 flex-shrink-0"
                              checked={!!importSelected[sec.key]}
                              onChange={e => setImportSelected(prev => ({ ...prev, [sec.key]: e.target.checked }))}
                              id={`imp-${sec.key}`}
                            />
                            <label htmlFor={`imp-${sec.key}`} className="fw-semibold mb-0" style={{ fontSize: 13, cursor: 'pointer' }}>
                              {sec.title || sec.key}
                            </label>
                          </div>
                          {sec.content && (
                            <div
                              className="ql-editor p-2 mt-1 border rounded"
                              style={{ background: '#fafafa', fontSize: 12, maxHeight: 120, overflowY: 'auto', pointerEvents: 'none' }}
                              dangerouslySetInnerHTML={{ __html: sec.content }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary btn-sm" onClick={() => setImportStep(null)} disabled={importApplying}>
                      {t('Cancel', 'Cancelar', lang)}
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleApplyImport}
                      disabled={importApplying || !Object.values(importSelected).some(Boolean)}
                    >
                      {importApplying
                        ? t('Applying…', 'Aplicando…', lang)
                        : `✅ ${t('Apply', 'Aplicar', lang)} ${Object.values(importSelected).filter(Boolean).length} ${t('sections', 'secciones', lang)}`}
                    </button>
                  </div>
                </>
              )}

              {importStep === 'upload' && (
                <div className="modal-footer">
                  <button className="btn btn-secondary btn-sm" onClick={() => setImportStep(null)}>
                    {t('Cancel', 'Cancelar', lang)}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Generate Section modal */}
      {showGenerate && activeSection && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className={`modal-dialog modal-dialog-centered ${generateStep === 'preview' ? 'modal-xl' : 'modal-lg'}`}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">✨ {t('Generate', 'Generar', lang)}: {activeSection.title}</h5>
                <button className="btn-close" onClick={() => setShowGenerate(false)} disabled={generateStep === 'running'} />
              </div>
              <div className="modal-body">
                {generateStep === 'idle' && (
                  <>
                    <p className="text-muted small mb-3">
                      {t(
                        'Claude will write this section using your company profile, assigned products, and other completed sections as context.',
                        'Claude escribirá esta sección usando tu perfil de empresa, productos asignados y otras secciones completadas como contexto.',
                        lang
                      )}
                    </p>
                    <label className="form-label fw-semibold">
                      {t('Specific instructions (optional)', 'Instrucciones específicas (opcional)', lang)}
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={generateNotes}
                      onChange={e => setGenerateNotes(e.target.value)}
                      placeholder={t(
                        'E.g. "Focus on allergen cross-contact risks. Include temperature monitoring for CCP1."',
                        'Ej. "Enfócate en riesgos de contaminación cruzada por alérgenos. Incluye monitoreo de temperatura para PCC1."',
                        lang
                      )}
                      autoFocus
                    />
                  </>
                )}
                {generateStep === 'running' && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary mb-3" />
                    <div className="fw-semibold">{t('Generating content…', 'Generando contenido…', lang)}</div>
                    <div className="text-muted small mt-1">{generateProgress}</div>
                  </div>
                )}
                {generateStep === 'preview' && (
                  <div>
                    <div className="alert alert-success py-2 small mb-3">
                      {t('Review the generated content below. Click "Apply to Editor" to use it, or regenerate with different instructions.',
                         'Revisa el contenido generado. Haz clic en "Aplicar al Editor" para usarlo, o genera nuevamente con otras instrucciones.', lang)}
                    </div>
                    {activeSection?.contentType === 'table_json' ? (
                      <div className="border rounded p-2" style={{ background: '#fff', maxHeight: 400, overflowY: 'auto' }}>
                        <TableJsonPreview value={generateDraft} />
                      </div>
                    ) : (
                      <div
                        className="ql-editor border rounded p-3"
                        style={{ background: '#fff', maxHeight: 400, overflowY: 'auto' }}
                        dangerouslySetInnerHTML={{ __html: generateDraft }}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowGenerate(false)} disabled={generateStep === 'running'}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                {generateStep === 'preview' && (
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setGenerateStep('idle')}>
                    {t('Regenerate', 'Regenerar', lang)}
                  </button>
                )}
                {generateStep !== 'running' && generateStep !== 'preview' && (
                  <button className="btn btn-primary btn-sm" onClick={handleGenerate} disabled={generateStep === 'running'}>
                    ✨ {t('Generate', 'Generar', lang)}
                  </button>
                )}
                {generateStep === 'preview' && (
                  <button className="btn btn-primary btn-sm" onClick={handleApplyGenerate}>
                    ✅ {t('Apply to Editor', 'Aplicar al Editor', lang)}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Policy picker modal */}
      {showPolicyPicker && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔗 {t('Link to SQF Policy', 'Vincular a Política SQF', lang)}</h5>
                <button className="btn-close" onClick={() => setShowPolicyPicker(false)} disabled={linkingSaving} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  {t(
                    'Select a policy to link to this section. The section will display the policy\'s live content and cannot be edited directly until unlinked.',
                    'Selecciona una política para vincular a esta sección. La sección mostrará el contenido actualizado de la política y no podrá editarse directamente hasta ser desvinculada.',
                    lang
                  )}
                </p>
                <input
                  className="form-control mb-3"
                  placeholder={t('Search policies…', 'Buscar políticas…', lang)}
                  value={policySearch}
                  onChange={e => setPolicySearch(e.target.value)}
                  autoFocus
                />
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {policies
                    .filter(p => !p.archivedAt)
                    .filter(p => {
                      const q = policySearch.toLowerCase();
                      return !q || p.name?.toLowerCase().includes(q) || p.nameEs?.toLowerCase().includes(q);
                    })
                    .map(p => (
                      <button
                        key={p.id}
                        className="btn btn-light text-start w-100 mb-1 border"
                        style={{ fontSize: 13 }}
                        onClick={() => handleLinkPolicy(p.id)}
                        disabled={linkingSaving}
                      >
                        <div className="fw-semibold">{p.name}</div>
                        {p.nameEs && p.nameEs !== p.name && (
                          <div className="text-muted small">{p.nameEs}</div>
                        )}
                        {p.activeVersionId && (
                          <span className="badge bg-success ms-0 mt-1" style={{ fontSize: 9 }}>
                            {t('Published', 'Publicada', lang)}
                          </span>
                        )}
                      </button>
                    ))
                  }
                  {policies.filter(p => !p.archivedAt).length === 0 && (
                    <p className="text-muted text-center py-3">
                      {t('No policies found.', 'No se encontraron políticas.', lang)}
                    </p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowPolicyPicker(false)} disabled={linkingSaving}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval modal */}
      {showApproval && (
        <ApprovalModal
          planType={planType}
          lang={lang}
          onClose={() => setShowApproval(false)}
          onApproved={handleApproved}
        />
      )}

      {/* Product picker modal */}
      {showProductPicker && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📦 {t('Assign Product to Plan', 'Asignar Producto al Plan', lang)}</h5>
                <button className="btn-close" onClick={() => setShowProductPicker(false)} disabled={productSaving} />
              </div>
              <div className="modal-body">
                <input
                  className="form-control mb-3"
                  placeholder={t('Search products…', 'Buscar productos…', lang)}
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  autoFocus
                />
                {(() => {
                  const assignedIds = new Set(planProducts.map(p => p.productId));
                  const available = allProducts.filter(p =>
                    !assignedIds.has(p.id) &&
                    (!productSearch.trim() || p.name?.toLowerCase().includes(productSearch.toLowerCase()))
                  );
                  const selCount = Object.values(pickerSelected).filter(Boolean).length;
                  if (!available.length) return (
                    <p className="text-muted text-center py-3">
                      {productSearch ? t('No matching products.', 'No hay productos que coincidan.', lang) : t('All products are already assigned.', 'Todos los productos ya están asignados.', lang)}
                    </p>
                  );
                  return (
                    <>
                      <div className="d-flex gap-2 mb-2">
                        <button className="btn btn-link btn-sm p-0" onClick={() => {
                          const sel = {};
                          available.forEach(p => { sel[p.id] = true; });
                          setPickerSelected(sel);
                        }}>{t('Select all', 'Seleccionar todo', lang)}</button>
                        <button className="btn btn-link btn-sm p-0" onClick={() => setPickerSelected({})}>
                          {t('Clear', 'Limpiar', lang)}
                        </button>
                        {selCount > 0 && <span className="text-muted small align-self-center">{selCount} {t('selected', 'seleccionados', lang)}</span>}
                      </div>
                      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {available.map(p => (
                          <div
                            key={p.id}
                            className="d-flex align-items-start gap-2 p-2 rounded mb-1 border"
                            style={{ cursor: 'pointer', background: pickerSelected[p.id] ? '#e8f0fe' : '#fff' }}
                            onClick={() => setPickerSelected(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input mt-0 flex-shrink-0"
                              checked={!!pickerSelected[p.id]}
                              readOnly
                              style={{ cursor: 'pointer' }}
                            />
                            <div>
                              <div className="fw-semibold" style={{ fontSize: 13 }}>{p.name}</div>
                              {p.description && <div className="text-muted" style={{ fontSize: 12 }}>{p.description}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowProductPicker(false)} disabled={productSaving}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleAssignProducts}
                  disabled={productSaving || !Object.values(pickerSelected).some(Boolean)}
                >
                  {productSaving
                    ? t('Assigning…', 'Asignando…', lang)
                    : `+ ${t('Assign', 'Asignar', lang)} ${Object.values(pickerSelected).filter(Boolean).length || ''} ${t('product(s)', 'producto(s)', lang)}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product details edit modal */}
      {editingProduct && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">✏️ {editingProduct.name}</h5>
                <button className="btn-close" onClick={() => { setEditingProduct(null); setRulesResult(null); }} disabled={productSaving} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  {t('Add food safety details for this product. These fields are used when auto-generating product description sections.',
                     'Agrega detalles de inocuidad para este producto. Estos campos se usan al auto-generar las secciones de descripción de producto.', lang)}
                </p>
                {[
                  ['intendedUse',          t('Intended Use',     'Uso Previsto',       lang), t('e.g. Direct consumption, retail sale', 'ej. Consumo directo, venta al por menor', lang)],
                  ['distributionMethod',   t('Distribution Method', 'Método de Distribución', lang), t('e.g. Refrigerated truck, ambient', 'ej. Camión refrigerado, temperatura ambiente', lang)],
                  ['storageConds',         t('Storage Conditions', 'Condiciones de Almacenamiento', lang), t('e.g. Frozen at -18°C, refrigerated', 'ej. Congelado a -18°C, refrigerado', lang)],
                  ['shelfLife',            t('Shelf Life',       'Vida Útil',          lang), t('e.g. 12 months, 7 days', 'ej. 12 meses, 7 días', lang)],
                  ['regulatoryCategory',   t('Regulatory Category', 'Categoría Regulatoria', lang), t('e.g. FDA 21 CFR 117, USDA FSIS', 'ej. FDA 21 CFR 117, USDA FSIS', lang)],
                ].map(([field, label, placeholder]) => (
                  <div className="mb-3" key={field}>
                    <label className="form-label fw-semibold small">{label}</label>
                    <input
                      className="form-control form-control-sm"
                      placeholder={placeholder}
                      value={editingProduct[field]}
                      onChange={e => setEditingProduct(ep => ({ ...ep, [field]: e.target.value }))}
                    />
                  </div>
                ))}

                {/* Ingredients — required, pre-filled from catalog */}
                <div className="mb-3">
                  <label className="form-label fw-semibold small">
                    🥩 {t('Ingredients', 'Ingredientes', lang)} <span className="text-danger">*</span>
                  </label>
                  <div className="alert py-2 px-2 mb-2 d-flex align-items-start gap-2" style={{ background: '#fff8e1', border: '1px solid #ffe082', fontSize: 12 }}>
                    <span>⚠️</span>
                    <span>
                      {t(
                        'To add, remove, or update ingredients and sub-ingredients, go to the',
                        'Para agregar, eliminar o actualizar ingredientes y sub-ingredientes, ve a la',
                        lang
                      )}{' '}
                      <strong>{t('Product page', 'página del Producto', lang)}</strong>{' '}
                      {t('in Inventory and make the changes there. The values below are read from the catalog.',
                         'en Inventario y realiza los cambios allí. Los valores a continuación se leen del catálogo.',
                         lang)}
                    </span>
                  </div>
                  {editingProduct.productIngredients?.length > 0 && (
                    <div className="mb-2 p-2 rounded" style={{ background: '#f8f9fa', fontSize: 12, border: '1px solid #dee2e6' }}>
                      <div className="text-muted mb-1 fw-semibold">{t('From product catalog (hover for sub-ingredients):', 'Del catálogo de productos (pasar el cursor para ver sub-ingredientes):', lang)}</div>
                      <div className="d-flex flex-wrap gap-1">
                        {editingProduct.productIngredients.map(pi => {
                          const ing = pi.Ingredient;
                          if (!ing) return null;
                          const subs = Array.isArray(ing.subIngredients) ? ing.subIngredients : [];
                          return (
                            <span key={ing.id} className="badge bg-white text-dark border" style={{ fontSize: 11 }}
                              title={subs.length > 0 ? subs.map(s => s.name).join(', ') : undefined}>
                              {ing.name}{subs.length > 0 ? ` ↳${subs.length}` : ''}
                            </span>
                          );
                        }).filter(Boolean)}
                      </div>
                    </div>
                  )}
                  <textarea
                    className={`form-control form-control-sm${!editingProduct.ingredients?.trim() ? ' border-warning' : ''}`}
                    rows={3}
                    placeholder={t(
                      'Enter ingredients as they appear in food safety documentation, comma separated (e.g. Wheat flour, Sugar, Salt, Whole milk)',
                      'Ingresa los ingredientes como aparecen en la documentación de inocuidad, separados por coma (ej. Harina de trigo, Azúcar, Sal, Leche entera)',
                      lang
                    )}
                    value={editingProduct.ingredients || ''}
                    onChange={e => setEditingProduct(ep => ({ ...ep, ingredients: e.target.value }))}
                  />
                  <div className="form-text small" style={{ color: editingProduct.ingredients?.trim() ? '#6c757d' : '#e65100' }}>
                    {editingProduct.ingredients?.trim()
                      ? t('Used for allergen matrix and AI-generated product descriptions. Will not be modified by AI.', 'Usado para la matriz de alérgenos y descripciones generadas por IA. La IA no los modificará.', lang)
                      : `⚠ ${t('Required — used for allergen matrix and product descriptions. The AI will not invent ingredients.', 'Requerido — usado para la matriz de alérgenos y descripciones de producto. La IA no inventará ingredientes.', lang)}`
                    }
                  </div>
                </div>

                {/* Allergens */}
                <div className="mb-3">
                  <label className="form-label fw-semibold small">🌾 {t('Allergens', 'Alérgenos', lang)}</label>
                  {(() => {
                    const detected = [];
                    (editingProduct.productIngredients || []).forEach(pi => {
                      const flags = pi.Ingredient?.allergenFlags;
                      if (!flags) return;
                      try {
                        const parsed = typeof flags === 'string' ? JSON.parse(flags) : flags;
                        if (Array.isArray(parsed)) parsed.forEach(f => { if (f && !detected.includes(f)) detected.push(f); });
                        else if (typeof parsed === 'string' && parsed.trim() && !detected.includes(parsed.trim())) detected.push(parsed.trim());
                      } catch { if (typeof flags === 'string' && flags.trim() && !detected.includes(flags.trim())) detected.push(flags.trim()); }
                    });
                    return detected.length > 0 ? (
                      <div className="mb-2">
                        <div className="text-muted small mb-1">{t('Detected from ingredients:', 'Detectados en ingredientes:', lang)}</div>
                        <div className="d-flex flex-wrap gap-1">
                          {detected.map(a => (
                            <span key={a} className="badge" style={{ background: '#fff3e0', color: '#e65100', fontSize: 11, border: '1px solid #ffcc80' }}>{a}</span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted small mb-2">{t('No allergens detected from ingredient list.', 'No se detectaron alérgenos en los ingredientes.', lang)}</div>
                    );
                  })()}
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    placeholder={t('Override or add allergens (e.g. Contains: Wheat, Milk, Soy)', 'Sobreescribir o agregar alérgenos (ej. Contiene: Trigo, Leche, Soya)', lang)}
                    value={editingProduct.allergens}
                    onChange={e => setEditingProduct(ep => ({ ...ep, allergens: e.target.value }))}
                  />
                  <div className="form-text small">{t('Overrides detected allergens in the plan document.', 'Sobreescribe los alérgenos detectados en el documento del plan.', lang)}</div>
                </div>

                {/* AI Rule Checker */}
                <div className="border-top pt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="fw-semibold small">🔍 {t('Applicable Regulations', 'Regulaciones Aplicables', lang)}</div>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => { setRulesResult(null); handleCheckRules(); }}
                      disabled={rulesResult?.loading}
                    >
                      {rulesResult?.loading ? (
                        <><span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10 }} />{t('Checking…', 'Verificando…', lang)}</>
                      ) : `🔍 ${t('Check Rules', 'Verificar Reglas', lang)}`}
                    </button>
                  </div>
                  {!rulesResult && (
                    <div className="text-muted small">{t('Run a quick AI scan to find 4–5 regulations relevant to this product.', 'Ejecuta una verificación rápida para encontrar 4–5 regulaciones relevantes a este producto.', lang)}</div>
                  )}
                  {rulesResult?.error && (
                    <div className="alert alert-danger py-2 small mt-1">{rulesResult.error}</div>
                  )}
                  {rulesResult && !rulesResult.loading && !rulesResult.error && rulesResult.rules?.length > 0 && (
                    <div>
                      {rulesResult.rules.map((r, i) => (
                        <div key={i} className="mb-2 p-2 border rounded" style={{ background: '#f8f9fa', fontSize: 12 }}>
                          <div className="fw-semibold">{r.rule}{r.title ? ` — ${r.title}` : ''}</div>
                          {r.relevance && <div className="text-muted mt-1">{r.relevance}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {rulesResult && !rulesResult.loading && !rulesResult.error && rulesResult.rules?.length === 0 && (
                    <div className="text-muted small">{t('No specific rules found for this product.', 'No se encontraron reglas específicas para este producto.', lang)}</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => { setEditingProduct(null); setRulesResult(null); }} disabled={productSaving}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleSaveProductDetails} disabled={productSaving}>
                  {productSaving ? t('Saving…', 'Guardando…', lang) : t('Save Details', 'Guardar Detalles', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto-gen preview modal */}
      {autoGenPreview && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">👁 {t('Auto-Generated Preview', 'Vista Previa Auto-Generada', lang)}</h5>
                {autoGenPreview.step === 'done' && (
                  <button className="btn-close" onClick={() => setAutoGenPreview(null)} />
                )}
              </div>
              <div className="modal-body">
                {autoGenPreview.step === 'running' ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" />
                    <div className="fw-semibold">{t('Generating preview…', 'Generando vista previa…', lang)}</div>
                    <div className="text-muted small mt-1">{autoGenPreview.progress}</div>
                  </div>
                ) : (
                  <div>
                    <div className="alert alert-info py-2 small mb-3">
                      {t('Preview generated from assigned products. Click "Apply to Editor" to save this as a manual override.',
                         'Vista previa generada de los productos asignados. Haz clic en "Aplicar al Editor" para guardarla como edición manual.', lang)}
                    </div>
                    <div
                      className="ql-editor border rounded p-3"
                      style={{ background: '#fff' }}
                      dangerouslySetInnerHTML={{ __html: autoGenPreview.content }}
                    />
                  </div>
                )}
              </div>
              {autoGenPreview.step === 'done' && (
                <div className="modal-footer">
                  <button className="btn btn-secondary btn-sm" onClick={() => setAutoGenPreview(null)}>
                    {t('Close', 'Cerrar', lang)}
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => handleApplyAutoGen(autoGenPreview.content)}>
                    ✅ {t('Apply to Editor', 'Aplicar al Editor', lang)}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Share plan modal */}
      {showShareModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔗 {t('Share Plan', 'Compartir Plan', lang)}</h5>
                <button className="btn-close" onClick={() => setShowShareModal(false)} disabled={shareSaving} />
              </div>
              <div className="modal-body">

                {/* Create new link */}
                <div className="card mb-4">
                  <div className="card-body">
                    <h6 className="fw-semibold mb-3">{t('Create New Share Link', 'Crear Nuevo Enlace', lang)}</h6>
                    <div className="mb-3">
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Expiry', 'Vencimiento', lang)}</label>
                      <div className="d-flex gap-3">
                        {[15, 30].map(days => (
                          <div key={days} className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              id={`exp-${days}`}
                              checked={shareExpiry === days}
                              onChange={() => setShareExpiry(days)}
                              disabled={shareSaving}
                            />
                            <label className="form-check-label" htmlFor={`exp-${days}`} style={{ fontSize: 13 }}>
                              {days} {t('days', 'días', lang)}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Note (optional)', 'Nota (opcional)', lang)}</label>
                      <input
                        className="form-control form-control-sm"
                        value={shareNote}
                        onChange={e => setShareNote(e.target.value)}
                        placeholder={t('e.g. For auditor John Smith', 'ej. Para el auditor Juan Gómez', lang)}
                        disabled={shareSaving}
                      />
                    </div>
                    {shareCreated ? (
                      <div>
                        <div className="alert alert-success py-2 mb-2">
                          <div className="fw-semibold mb-1">✅ {t('Link created!', '¡Enlace creado!', lang)}</div>
                          <div className="d-flex gap-2 align-items-center">
                            <input
                              className="form-control form-control-sm"
                              value={shareCreated}
                              readOnly
                              onClick={e => e.target.select()}
                              style={{ fontSize: 11, fontFamily: 'monospace' }}
                            />
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => { navigator.clipboard.writeText(shareCreated); showToast(t('Copied!', '¡Copiado!', lang)); }}
                            >
                              {t('Copy', 'Copiar', lang)}
                            </button>
                          </div>
                        </div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setShareCreated(null)}>
                          {t('Create Another', 'Crear Otro', lang)}
                        </button>
                      </div>
                    ) : (
                      <button className="btn btn-primary btn-sm" onClick={handleCreateShareLink} disabled={shareSaving}>
                        {shareSaving ? t('Creating…', 'Creando…', lang) : `🔗 ${t('Generate Link', 'Generar Enlace', lang)}`}
                      </button>
                    )}
                  </div>
                </div>

                {/* Existing links */}
                <h6 className="fw-semibold mb-2">{t('Active Links', 'Enlaces Activos', lang)}</h6>
                {shareLinksLoading ? (
                  <div className="text-center py-3 text-muted"><div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}</div>
                ) : shareLinks.length === 0 ? (
                  <div className="text-muted small py-2">{t('No share links yet.', 'Sin enlaces compartidos.', lang)}</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {shareLinks.map(link => {
                      const url = `${window.location.origin}/share/food-safety/${link.token}`;
                      const expired = new Date() > new Date(link.expiresAt);
                      const revoked = !!link.revokedAt;
                      const active = !expired && !revoked;
                      return (
                        <div key={link.token} className={`border rounded p-2 ${active ? '' : 'opacity-50'}`} style={{ fontSize: 12 }}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="d-flex gap-2 align-items-center mb-1 flex-wrap">
                                {revoked
                                  ? <span className="badge bg-danger">Revoked</span>
                                  : expired
                                    ? <span className="badge bg-secondary">Expired</span>
                                    : <span className="badge bg-success">Active</span>
                                }
                                {link.note && <span className="text-muted">{link.note}</span>}
                              </div>
                              <div className="text-muted" style={{ fontSize: 11 }}>
                                {t('Expires', 'Vence', lang)}: {new Date(link.expiresAt).toLocaleDateString()}
                                {link.CreatedBy && ` · ${t('by', 'por', lang)} ${link.CreatedBy.name || link.CreatedBy.email}`}
                              </div>
                              {active && (
                                <div className="d-flex gap-1 mt-1">
                                  <input
                                    className="form-control form-control-sm"
                                    value={url}
                                    readOnly
                                    onClick={e => e.target.select()}
                                    style={{ fontSize: 10, fontFamily: 'monospace', maxWidth: 340 }}
                                  />
                                  <button
                                    className="btn btn-outline-secondary btn-sm"
                                    style={{ fontSize: 10, padding: '1px 6px' }}
                                    onClick={() => { navigator.clipboard.writeText(url); showToast(t('Copied!', '¡Copiado!', lang)); }}
                                  >
                                    {t('Copy', 'Copiar', lang)}
                                  </button>
                                </div>
                              )}
                            </div>
                            {active && (
                              <button
                                className="btn btn-outline-danger btn-sm ms-2"
                                style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                                disabled={revoking === link.token}
                                onClick={() => handleRevokeShareLink(link.token)}
                              >
                                {revoking === link.token ? '…' : t('Revoke', 'Revocar', lang)}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowShareModal(false)}>
                  {t('Close', 'Cerrar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmModal && (
        <ConfirmModal
          {...confirmModal}
          onClose={() => setConfirmModal(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`alert alert-${toast.variant} alert-dismissible mb-0`}
          style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, minWidth: 280, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
        >
          {toast.message}
          <button type="button" className="btn-close" onClick={() => setToast(null)} />
        </div>
      )}
    </div>
  );
}
